import { Button, Dropdown, Edit, theme } from '@lmc/ui-kit'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { FC, useState } from 'react'
import { ServiceUserInfo } from 'screens/Rostering/RotaBuilder'
import styled from 'styled-components'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import useUser from 'hooks/useUser/useUser'
import { getLastChangedBy, openGoogleMaps } from 'screens/Rostering/utils'
import { getNewAddress } from 'screens/Rostering/RotaPattern/utils'
import { AddressType, Carer, UserRole } from '@lmc/ts-common'
import { DrawerConfirmAction } from './DrawerConfirmAction'
import { capitalize } from 'lodash'
import { Form as AntdForm, Tooltip } from 'antd'
import { CancelEventForm } from './CancelEventForm'
import EditEventModal from './EditEventModal'
import {
  DAY_FIRST_DATE_FORMAT,
  TIME_FORMAT,
  getDateTimeInZone
} from 'screens/Rostering/utils.datetime'
import useRevisionUpdate from 'screens/Rostering/hooks/useRevisionUpdate'
import useRevisionCancel from 'screens/Rostering/hooks/useRevisionCancel'
import { SHIFT, VISIT } from 'screens/Rostering/constants'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import DeletedServiceUser from 'screens/Rostering/RotaDrawer/components/DeletedServiceUser'
import { RotaInstanceStatus } from 'screens/Rostering/types'
import { ExclamationCircleFilled } from '@ant-design/icons'
import { useSelector } from 'react-redux'

type DrawerAboutProps = {
  revisionDetail: any
  publishedRota?: boolean
}

export const DrawerAbout: FC<DrawerAboutProps> = ({
  revisionDetail,
  publishedRota
}) => {
  const { t } = useTranslation()

  const { user: updatedByUser } = useUser({
    id: revisionDetail?.updatedBy?.id
  })

  const type = revisionDetail?.siteShift ? SHIFT : VISIT

  const [editModalVisible, setEditModalVisible] = useState(false)

  const address = revisionDetail?.address?.find(
    (item: { id: string }) => item.id === revisionDetail?.addressId
  )

  const addressType = AddressType[address?.type]?.replace(/_/g, ' ')

  const addressLabel = getNewAddress(address, false)

  const isAddressValid = revisionDetail?.isAddressValid

  const home = useSelector(
    (state: { global: { home: any } }) => state.global.home
  )
  const { hasRota } = useHomeType()
  const geofencingFlags = useFeatureFlags({ flagNames: ['geofencing'] })?.data
  const geofencingEnabled = geofencingFlags?.geofencing?.enabled

  const isGeofencingEnabled =
    (hasRota ?? false) &&
    (home?.rotaSettings as { geofencing?: boolean } | undefined)?.geofencing ===
      true &&
    geofencingEnabled === true

  const isAddressInvalidForGeofencing = address
    ? isGeofencingEnabled &&
      (address.notValidForGeofencing === true ||
        address.lat == null ||
        address.lng == null)
    : false

  const showAddressWarning = !isAddressValid || isAddressInvalidForGeofencing

  const isCancelled = revisionDetail?.status === 'cancelled'

  const [cancelForm] = AntdForm.useForm()

  const [canCancelEvent, setCanCancelEvent] = useState(false)

  const isDeletedSU = !!revisionDetail?.deletedServiceUser

  const handleCancelRevision = (values?: any) => {
    cancelRevision?.(values)
  }

  const cancelLabel = t(
    revisionDetail?.siteShift ? 'CANCEL_SHIFT' : 'CANCEL_VISIT'
  )

  const { updateRevision, isLoading: isLoadingUpdate } = useRevisionUpdate({})

  const { cancelRevision, isCancelRevisionLoading } = useRevisionCancel({
    id: revisionDetail?.id
  })

  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )

  const isCareHomeAdmin = userRole === UserRole.careHomeAdmin

  const flags = useFeatureFlags({
    flagNames: [
      'rostering_parked_features',
      'rostering_editing_limitations',
      'rostering_admin_cancelling_flexibility'
    ]
  })?.data

  const flagsRosteringParkedFeaturesEnabled =
    flags?.rostering_parked_features?.enabled

  const flagsRosteringEditingLimitations =
    flags?.rostering_editing_limitations?.enabled

  const flagsRosteringAdminCancellingFlexibility =
    flags?.rostering_admin_cancelling_flexibility?.enabled

  const isOvernight =
    getDateTimeInZone(revisionDetail.startTime).date() <
    getDateTimeInZone(revisionDetail.endTime).date()

  const isInProgress =
    revisionDetail?.status === RotaInstanceStatus.IN_PROGRESS ||
    revisionDetail?.clockIns?.length > 0

  const isDone = revisionDetail?.status === RotaInstanceStatus.DONE

  const isOverdue = revisionDetail?.status === RotaInstanceStatus.OVERDUE

  const canCancelEventByAdmin = isOverdue
    ? flagsRosteringAdminCancellingFlexibility && isCareHomeAdmin
    : true

  const isCancellable = (() => {
    if (publishedRota) return false

    if (!flagsRosteringEditingLimitations) return true

    return (
      !isDeletedSU &&
      !isCancelled &&
      !isInProgress &&
      !isDone &&
      canCancelEventByAdmin
    )
  })()

  const getCancelEventTooltip = () => {
    if (flagsRosteringAdminCancellingFlexibility && !isCancellable) {
      if (isInProgress || isDone) {
        return t('CARER_CLOCKED_IN_CANCEL_TOOLTIP')
      }

      if (isOverdue && !isCareHomeAdmin) {
        return t('OVERDUE_EVENT_ADMIN_ONLY_TOOLTIP')
      }
    }
    return undefined
  }

  const isEditable =
    !publishedRota &&
    (flagsRosteringEditingLimitations ? !isDeletedSU && !isCancelled : true)

  if (!revisionDetail) {
    return null
  }

  const editMenuItems = [
    {
      key: 'editEvent',
      label: t('EDIT_THIS_EVENT')
    },
    {
      key: 'editPattern',
      label: revisionDetail.orphaned ? (
        <Tooltip title={t('EDIT_PATTERN_ORPHANED_TOOLTIP')}>
          {t('EDIT_PATTERN')}
        </Tooltip>
      ) : (
        t('EDIT_PATTERN')
      ),
      disabled: revisionDetail.orphaned
    }
  ]

  const handleCancelEvent = async () => {
    try {
      await cancelForm.validateFields()
      cancelForm.submit()
      setCanCancelEvent(true)
    } catch {
      setCanCancelEvent(false)
    }
  }

  const handleEditClick = ({ key }: { key: string }) => {
    if (key === 'editEvent') {
      setEditModalVisible(true)
    } else if (key === 'editPattern' && !revisionDetail.orphaned) {
      const id = revisionDetail?.patternId
      const type = revisionDetail?.siteShift ? 'shift' : 'visit'

      if (id) {
        window.open(`${window.location.origin}/rota/${id}/${type}`, '_blank')
      }
    }
  }

  return (
    <DetailsWrapper data-cy="drawer-about-details">
      <HeadingWrapper>
        <Heading level={3} data-cy="drawer-about-heading">
          {t('ABOUT')}
        </Heading>
        {isEditable && (
          <Dropdown
            menu={{
              items: editMenuItems,
              onClick: handleEditClick
            }}
            trigger={['click']}
          >
            <Button variant="text">
              <Edit />
              {t('EDIT')}
            </Button>
          </Dropdown>
        )}
      </HeadingWrapper>
      <HeaderWrapper>
        {type === VISIT &&
          (isDeletedSU ? (
            <DeletedServiceUser />
          ) : (
            <ServiceUserInfo
              serviceUser={revisionDetail?.resident}
              avatarSize={40}
            />
          ))}
        <InfoSectionWrapperRow>
          <InfoSection>
            <StyledText level={2}>{t('TIME_WINDOW')}</StyledText>
            <FlexiSection>
              <HoursSection>
                <Text weight="bold" data-cy="drawer-about-time-window">
                  {`${getDateTimeInZone(revisionDetail?.startTime).format(
                    TIME_FORMAT
                  )} - ${getDateTimeInZone(revisionDetail?.endTime).format(
                    TIME_FORMAT
                  )}`}
                </Text>
                {isOvernight && (
                  <OvernightText level={2}>{t('PLUS_ONE')}</OvernightText>
                )}
              </HoursSection>
              {flagsRosteringParkedFeaturesEnabled &&
                revisionDetail?.flexiType && (
                  <Text level={2}>· {t('FLEXI')}</Text>
                )}
            </FlexiSection>
          </InfoSection>
          <InfoSection>
            <StyledText level={2}>{t('PLANNED_DURATION')}</StyledText>
            <Text weight="bold" data-cy="drawer-about-planned-duration">
              {revisionDetail?.expectedDuration}
            </Text>
          </InfoSection>
          <InfoSection>
            <StyledText level={2}>{t('DATE')}</StyledText>
            <Text weight="bold" data-cy="drawer-about-date">
              {getDateTimeInZone(revisionDetail?.startTime).format(
                DAY_FIRST_DATE_FORMAT
              )}
            </Text>
          </InfoSection>
          {type === VISIT && (
            <InfoSection>
              <StyledText level={2}>{t('FUNDING_TYPE')}</StyledText>
              <Text weight="bold">
                {isDeletedSU ? t('ANONYMISED') : revisionDetail?.fundingType}
              </Text>
            </InfoSection>
          )}
        </InfoSectionWrapperRow>
      </HeaderWrapper>
      <InnerWrapper>
        {type === VISIT && (
          <SectionWrapper>
            <StyledSectionTitleText level={2}>
              {t('ADDRESS')}
            </StyledSectionTitleText>

            {isDeletedSU ? (
              <Text>{t('ANONYMISED')}</Text>
            ) : (
              <>
                <AddressRow>
                  <AddressSection onClick={() => openGoogleMaps(addressLabel)}>
                    <Text>{addressLabel}</Text>
                    {showAddressWarning && (
                      <Tooltip
                        placement="bottom"
                        color={theme.color.neutral.color3}
                        title={
                          <ContentContainer>
                            <Text>
                              {t('INVALID_ADDRESS')}{' '}
                              <UnderlinedLink
                                href="https://support.logmycare.co.uk/en/articles/11011338-rostering-travel-time-and-mileage "
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t('HERE')}
                              </UnderlinedLink>
                              .
                            </Text>
                          </ContentContainer>
                        }
                        overlayInnerStyle={{
                          width: '300px',
                          padding: '8px 16px'
                        }}
                      >
                        <StyledExclamationCircleFilled />
                      </Tooltip>
                    )}
                  </AddressSection>
                  {!publishedRota && showAddressWarning && (
                    <Link
                      to={`${window.location.origin}/residents/${revisionDetail?.subjectId}/contact-details`}
                      target="_blank"
                    >
                      <Button variant="text">
                        <Edit />
                        {t('EDIT_ADDRESS')}
                      </Button>
                    </Link>
                  )}
                </AddressRow>
                <StyledSectionSubTitleText>
                  {addressType}
                </StyledSectionSubTitleText>
              </>
            )}
          </SectionWrapper>
        )}

        {revisionDetail?.description && (
          <SectionWrapper>
            <StyledSectionTitleText level={2}>
              {t('VISIT_DESC', { variable: capitalize(type) })}
            </StyledSectionTitleText>
            <Text>
              {isDeletedSU ? t('ANONYMISED') : revisionDetail?.description}
            </Text>
          </SectionWrapper>
        )}
        <Divider />
        {isCancelled && (
          <>
            <SectionWrapper>
              <StyledSectionTitleText level={2}>
                {t('WHO_CAUSED_CANCELLATION')}
              </StyledSectionTitleText>
              <Text>{revisionDetail?.cancelledBy}</Text>
            </SectionWrapper>
            <SectionWrapper>
              <StyledSectionTitleText level={2}>
                {t('CANCEL_REASON')}
              </StyledSectionTitleText>
              <Text>{revisionDetail?.cancelledReason}</Text>
            </SectionWrapper>
            <SectionWrapper>
              <StyledSectionTitleText level={2}>
                {t('FINANCIAL_IMPACT')}
              </StyledSectionTitleText>
              <Text>{revisionDetail?.cancelledFinancialImpact}</Text>
            </SectionWrapper>
            {revisionDetail?.cancelledPreventable && (
              <SectionWrapper>
                <StyledSectionTitleText level={2}>
                  {t('PREVENTION')}
                </StyledSectionTitleText>
                <Text>{t('CANCELLATION_PREVENTED')}</Text>
              </SectionWrapper>
            )}
            <SectionWrapper>
              <StyledSectionTitleText level={2}>
                {t('CANCEL_NOTES')}
              </StyledSectionTitleText>
              <Text>{revisionDetail?.cancelledNotes}</Text>
            </SectionWrapper>
          </>
        )}
        <LastChangedWrapper>
          <SectionWrapper style={{ minWidth: '50%' }}>
            <StyledSectionTitleText level={2}>
              {t('LAST_UPDATED_BY')}
            </StyledSectionTitleText>
            <Text>{getLastChangedBy(updatedByUser, revisionDetail)}</Text>
          </SectionWrapper>
          {/* <SectionWrapper>
            <StyledSectionTitleText level={2}>
              {t('SEEN_BY')}
            </StyledSectionTitleText>
            <Avatar.Group>
              <Avatar src="https://api.dicebear.com/7.x/miniavs/svg?seed=1" />
              <a href="https://ant.design">
                <Avatar style={{ backgroundColor: '#f56a00' }}>K</Avatar>
              </a>
              <Tooltip title="Ant User" placement="top">
                <Avatar
                  style={{ backgroundColor: '#87d068' }}
                  icon={<UserOutlined />}
                />
              </Tooltip>
              <Avatar
                style={{ backgroundColor: '#1677ff' }}
                icon={<AntDesignOutlined />}
              />
            </Avatar.Group>
      </SectionWrapper> */}
        </LastChangedWrapper>
        <Divider />
        <ButtonsWrapper>
          {!revisionDetail?.orphaned && !isDeletedSU && (
            <Link
              to={`${window.location.origin}/rota/${revisionDetail?.patternId}/${type}`}
              target="_blank"
            >
              <Button>{t('VIEW_ROTA_PATTERN')}</Button>
            </Link>
          )}
          {!isCancelled &&
            (isCancellable || flagsRosteringAdminCancellingFlexibility) && (
              <DrawerConfirmAction
                buttonProps={{
                  title: cancelLabel,
                  variant:
                    flagsRosteringAdminCancellingFlexibility && !isCancellable
                      ? 'primary'
                      : 'danger'
                }}
                actionProps={{
                  title: t('ARE_CANCEL_TITLE', { variable: type }),
                  content: t('ARE_CANCEL_DESC'),
                  confirmTitle: cancelLabel
                }}
                onConfirm={handleCancelEvent}
                isLoading={isCancelRevisionLoading}
                canNavigate={canCancelEvent}
                disabled={
                  flagsRosteringAdminCancellingFlexibility && !isCancellable
                }
                tooltip={getCancelEventTooltip()}
              >
                <CancelEventForm
                  form={cancelForm}
                  onFinish={handleCancelRevision}
                />
              </DrawerConfirmAction>
            )}
        </ButtonsWrapper>
      </InnerWrapper>
      <EditEventModal
        editModalVisible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        revisionDetail={revisionDetail}
        updateRevision={updateRevision}
        isLoadingUpdate={isLoadingUpdate}
      />
    </DetailsWrapper>
  )
}

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  color: ${(props) => props.theme.color.neutral.color3};
`

const HeadingWrapper = styled.div`
  display: flex;
  margin-top: 8px;
  align-items: center;
  justify-content: space-between;
`

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-top: auto;
  margin-bottom: 0px;
`

const StyledSectionTitleText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-left: 24px;
  margin-right: 24px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 185px;
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const FlexiSection = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background-color: ${(props) => props.theme.color.neutral.color10};
  border-radius: 20px;
`

const InfoSectionWrapperRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const StyledSectionSubTitleText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
  font-size: 12px;
  font-weight: 500;
`

const LastChangedWrapper = styled.div`
  display: flex;
  flex-direction: row;
`
const OvernightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  position: relative;
  top: 0;
  transform: translateY(-10%);
`
const HoursSection = styled.div`
  display: flex;
  gap: 4px;
`
const AddressSection = styled.div`
  text-decoration: underline;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: ${(props) => props.theme.color.neutral.color11};
`
const AddressRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  justify-content: space-between;
  cursor: pointer;
`
const StyledExclamationCircleFilled = styled(ExclamationCircleFilled)`
  color: ${(props) => props.theme.color.status.danger};
`
const UnderlinedLink = styled.a`
  text-decoration: underline;
  color: ${(props) => props.theme.color.status.primary};
`
